/**
 * Free Spins Overlay Component
 *
 * Displays free spins mode status including:
 * - Spins remaining counter
 * - Multiplier badge
 * - Accumulated wins during free spins
 * - Trigger celebration when starting
 * - Summary celebration when ending
 */
/* eslint-disable rumio-i18n/no-hardcoded-strings */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================================================
// Types
// =============================================================================

interface FreeSpinsOverlayProps {
  /** Whether free spins mode is active */
  isActive: boolean;
  /** Total free spins awarded */
  totalSpins: number;
  /** Remaining spins */
  spinsRemaining: number;
  /** Win multiplier */
  multiplier: number;
  /** Accumulated wins during free spins */
  totalWon: number;
  /** Whether currently spinning */
  isSpinning: boolean;
  /** Whether this is the first spin (show trigger animation) */
  showTrigger?: boolean;
  /** Called when trigger animation completes */
  onTriggerComplete?: () => void;
  /** Extra spins won (re-trigger) */
  extraSpinsWon?: number;
  /** Whether session is complete */
  isComplete?: boolean;
  /** Called when complete celebration finishes */
  onCompleteClose?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function FreeSpinsOverlay({
  isActive,
  totalSpins,
  spinsRemaining,
  multiplier,
  totalWon,
  isSpinning,
  showTrigger = false,
  onTriggerComplete,
  extraSpinsWon = 0,
  isComplete = false,
  onCompleteClose,
}: FreeSpinsOverlayProps) {
  const [showRetrigger, setShowRetrigger] = useState(false);

  // Show retrigger animation when extra spins won
  useEffect(() => {
    if (extraSpinsWon > 0) {
      // Schedule state update to avoid cascading renders
      const showTimer = requestAnimationFrame(() => {
        setShowRetrigger(true);
      });
      const hideTimer = setTimeout(() => setShowRetrigger(false), 2500);
      return () => {
        cancelAnimationFrame(showTimer);
        clearTimeout(hideTimer);
      };
    }
    return undefined;
  }, [extraSpinsWon]);

  if (!isActive && !showTrigger && !isComplete) return null;

  return (
    <>
      {/* Trigger Celebration */}
      <AnimatePresence>
        {showTrigger && (
          <TriggerCelebration
            spins={totalSpins}
            multiplier={multiplier}
            onComplete={onTriggerComplete}
          />
        )}
      </AnimatePresence>

      {/* Active Free Spins UI */}
      {isActive && !showTrigger && (
        <div className="absolute inset-x-0 top-0 z-20">
          {/* Header bar */}
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            className="bg-gradient-to-r from-purple-600/90 via-pink-500/90 to-purple-600/90 backdrop-blur-sm py-3 px-4 border-b border-purple-400/30"
          >
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {/* Spins Counter */}
              <div className="flex items-center gap-3">
                <motion.div
                  key={spinsRemaining}
                  initial={{ scale: 1.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <span className="text-pink-200 text-sm">FREE SPINS</span>
                  <span className="text-3xl font-bold text-white">
                    {spinsRemaining}
                  </span>
                  <span className="text-pink-200 text-sm">/ {totalSpins}</span>
                </motion.div>
              </div>

              {/* Multiplier Badge */}
              <motion.div
                animate={isSpinning ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.5, repeat: isSpinning ? Infinity : 0 }}
                className="bg-yellow-500 text-black font-bold px-4 py-1 rounded-full text-lg"
              >
                {multiplier}x
              </motion.div>

              {/* Total Won */}
              <div className="flex items-center gap-2">
                <span className="text-pink-200 text-sm">WON</span>
                <motion.span
                  key={totalWon}
                  initial={{ scale: 1.3 }}
                  animate={{ scale: 1 }}
                  className="text-2xl font-bold text-yellow-300"
                >
                  {totalWon.toLocaleString()}
                </motion.span>
              </div>
            </div>
          </motion.div>

          {/* Retrigger Animation */}
          <AnimatePresence>
            {showRetrigger && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                className="absolute inset-0 flex items-center justify-center bg-black/50 z-30"
              >
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  className="text-center"
                >
                  <div className="text-4xl font-bold text-yellow-400 mb-2">
                    +{extraSpinsWon} FREE SPINS!
                  </div>
                  <div className="text-xl text-white/80">RE-TRIGGER!</div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Complete Summary */}
      <AnimatePresence>
        {isComplete && (
          <CompleteSummary
            totalSpins={totalSpins}
            totalWon={totalWon}
            multiplier={multiplier}
            onClose={onCompleteClose}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// =============================================================================
// Trigger Celebration
// =============================================================================

interface TriggerCelebrationProps {
  spins: number;
  multiplier: number;
  onComplete?: () => void;
}

function TriggerCelebration({
  spins,
  multiplier,
  onComplete,
}: TriggerCelebrationProps) {
  const [phase, setPhase] = useState<'scatter' | 'spins' | 'ready'>('scatter');

  useEffect(() => {
    const scatterTimer = setTimeout(() => setPhase('spins'), 1500);
    const readyTimer = setTimeout(() => setPhase('ready'), 3000);
    const closeTimer = setTimeout(() => onComplete?.(), 4000);

    return () => {
      clearTimeout(scatterTimer);
      clearTimeout(readyTimer);
      clearTimeout(closeTimer);
    };
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    >
      <AnimatePresence mode="wait">
        {phase === 'scatter' && (
          <motion.div
            key="scatter"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            className="text-center"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="text-8xl mb-4"
            >
              ✨
            </motion.div>
            <div className="text-4xl font-bold text-yellow-400">
              SCATTER!
            </div>
          </motion.div>
        )}

        {phase === 'spins' && (
          <motion.div
            key="spins"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            exit={{ scale: 0 }}
            className="text-center"
          >
            <div className="text-6xl font-bold text-pink-400 mb-2">
              FREE SPINS!
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-8"
            >
              <div className="text-center">
                <div className="text-5xl font-bold text-white">{spins}</div>
                <div className="text-pink-300">SPINS</div>
              </div>
              <div className="text-4xl text-pink-300">@</div>
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-400">
                  {multiplier}x
                </div>
                <div className="text-pink-300">MULTIPLIER</div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {phase === 'ready' && (
          <motion.div
            key="ready"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-5xl font-bold text-green-400"
          >
            GET READY!
          </motion.div>
        )}
      </AnimatePresence>

      {/* Particles */}
      <FreeSpinParticles />
    </motion.div>
  );
}

// =============================================================================
// Complete Summary
// =============================================================================

interface CompleteSummaryProps {
  totalSpins: number;
  totalWon: number;
  multiplier: number;
  onClose?: () => void;
}

function CompleteSummary({
  totalSpins,
  totalWon,
  multiplier,
  onClose,
}: CompleteSummaryProps) {
  const [showCollect, setShowCollect] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowCollect(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        className="text-center bg-gradient-to-br from-purple-900/90 to-pink-900/90 p-8 rounded-2xl border border-purple-400/30 max-w-md mx-4"
      >
        <div className="text-xl text-pink-300 mb-2">FREE SPINS COMPLETE!</div>

        <div className="text-6xl font-bold text-yellow-400 mb-4">
          +{totalWon.toLocaleString()}
        </div>

        <div className="text-lg text-white/80 mb-4">
          from {totalSpins} spins @ {multiplier}x
        </div>

        <div className="flex justify-center gap-4 text-center mb-6">
          <div className="bg-purple-800/50 px-4 py-2 rounded-lg">
            <div className="text-2xl font-bold text-white">{totalSpins}</div>
            <div className="text-xs text-purple-300">SPINS</div>
          </div>
          <div className="bg-purple-800/50 px-4 py-2 rounded-lg">
            <div className="text-2xl font-bold text-yellow-400">{multiplier}x</div>
            <div className="text-xs text-purple-300">MULTIPLIER</div>
          </div>
        </div>

        <AnimatePresence>
          {showCollect && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={onClose}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-full hover:from-pink-400 hover:to-purple-400 transition-colors"
            >
              COLLECT
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>

      <FreeSpinParticles />
    </motion.div>
  );
}

// =============================================================================
// Particles Effect
// =============================================================================

function FreeSpinParticles() {
  const particles = Array.from({ length: 40 });
  const emojis = ['✨', '💫', '⭐', '🌟', '💜', '💖'];

  // Pre-generate random values for all particles once on mount
  const [particleData] = useState(() => {
    const generateRandom = () => Math.random();
    return particles.map(() => ({
      xTarget: 20 + generateRandom() * 60,
      yTarget: generateRandom() * 60,
      rotate: generateRandom() * 720,
      duration: 2 + generateRandom() * 2,
      delay: generateRandom() * 1,
      repeatDelay: generateRandom() * 2,
      emoji: emojis[Math.floor(generateRandom() * emojis.length)],
    }));
  });

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const data = particleData[i];
        return (
          <motion.div
            key={i}
            initial={{
              opacity: 0,
              x: '50%',
              y: '100%',
              scale: 0,
            }}
            animate={{
              opacity: [0, 1, 0],
              x: `${data.xTarget}%`,
              y: `${data.yTarget}%`,
              scale: [0, 1, 0.5],
              rotate: data.rotate,
            }}
            transition={{
              duration: data.duration,
              delay: data.delay,
              repeat: Infinity,
              repeatDelay: data.repeatDelay,
            }}
            className="absolute text-2xl"
          >
            {data.emoji}
          </motion.div>
        );
      })}
    </div>
  );
}

export default FreeSpinsOverlay;
